import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { UserPlus, Users2, MessageSquare, Wifi, WifiOff, Trash2, Play, Pause, Pencil, Plus, MessageCircle, Link, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import AppHeader from '../components/AppHeader';
import Breadcrumb from '../components/Breadcrumb';
import { theme } from '../styles/theme';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle,
  Card, Grid, Flex, Button, Badge,
  Input, Select, Label, FormGroup,
  ModalOverlay, ModalBox, ModalHeader, ModalTitle, ModalBody, ModalFooter,
  StatCard, StatValue, StatLabel,
  Spinner, LoadingContainer, EmptyState, EmptyIcon, EmptyText,
} from '../components/ui';
import api from '../services/api';
import WorkingHoursEditor, { EMPTY_WORKING_HOURS, whFromBackend, whToBackend } from '../components/WorkingHoursEditor';

const EMPTY_MEMBER = { name: '', email: '', role: '', phone: '', password: '', groups: [], workingHours: EMPTY_WORKING_HOURS };
const EMPTY_GROUP  = { name: '', members: [] };
const EMPTY_WA_GROUP = { connectorId: '', subject: '', description: '', joinApprovalMode: 'auto_approve', teamGroupId: '' };

const TeamManagement = () => {
  const { t } = useTranslation('team');
  const toast   = useToast();
  const confirm = useConfirm();
  const { tenantId } = useParams();

  const [tab, setTab]               = useState('members');
  const [members, setMembers]       = useState([]);
  const [teamGroups, setTeamGroups] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [waGroups, setWaGroups]     = useState([]);
  const [loading, setLoading]       = useState(true);

  // Modal membro
  const [memberModal, setMemberModal]   = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [memberForm, setMemberForm]     = useState(EMPTY_MEMBER);
  const [savingMember, setSavingMember] = useState(false);

  // Modal grupo interno
  const [groupModal, setGroupModal]   = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm]     = useState(EMPTY_GROUP);
  const [savingGroup, setSavingGroup] = useState(false);

  // Modal grupo WA
  const [waModal, setWaModal]       = useState(false);
  const [waForm, setWaForm]         = useState(EMPTY_WA_GROUP);
  const [savingWa, setSavingWa]     = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSallusAdmin = user.role === 'sallus_admin';
  const homeItem = isSallusAdmin
    ? { label: 'Libben Agents', path: '/sallus/dashboard' }
    : user.role === 'manager'
      ? { label: t('home', { ns: 'common' }), path: `/manager/${tenantId}` }
      : { label: t('home', { ns: 'common' }), path: `/dashboard/${tenantId}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [memRes, tgRes, connRes, wgRes] = await Promise.all([
        api.get(`/teams/${tenantId}/members`),
        api.get(`/wa-groups/${tenantId}/team-groups`),
        api.get(`/connectors/${tenantId}`),
        api.get(`/wa-groups/${tenantId}`),
      ]);
      setMembers(memRes.data.members || []);
      setTeamGroups(tgRes.data.teamGroups || []);
      setConnectors((connRes.data.connectors || []).filter(c => c.type === 'whatsapp' && c.enabled));
      setWaGroups(wgRes.data.groups || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  // ── Membros ────────────────────────────────────────────────────────────────
  const openCreateMember = () => { setEditingMember(null); setMemberForm(EMPTY_MEMBER); setMemberModal(true); };
  const openEditMember = (m) => {
    setEditingMember(m);
    // Deriva grupos a partir da fonte de verdade: teamGroup.members
    const memberGroups = teamGroups
      .filter(tg => (tg.members || []).includes(m.memberId))
      .map(tg => tg.teamGroupId);
    setMemberForm({ name: m.name || '', email: m.email || '', role: m.role || '', phone: m.phone || '', password: '', groups: memberGroups, workingHours: whFromBackend(m.workingHours) });
    setMemberModal(true);
  };
  const closeMemberModal = () => { setMemberModal(false); setEditingMember(null); setMemberForm(EMPTY_MEMBER); };

  const saveMember = async () => {
    if (!memberForm.name || !memberForm.email || !memberForm.role) { toast.warning(t('fillRequired')); return; }
    if (!editingMember && !memberForm.password) { toast.warning(t('passwordRequired')); return; }
    setSavingMember(true);
    try {
      if (editingMember) {
        const payload = { name: memberForm.name, role: memberForm.role, phone: memberForm.phone, groups: memberForm.groups, workingHours: whToBackend(memberForm.workingHours) };
        if (memberForm.password) payload.password = memberForm.password;
        const res = await api.put(`/teams/${tenantId}/members/${editingMember.memberId}`, payload);
        setMembers(prev => prev.map(a => a.memberId === editingMember.memberId ? { ...a, ...res.data.member } : a));
        toast.success(t('updateSuccess'));
      } else {
        const res = await api.post(`/teams/${tenantId}/members`, { ...memberForm, workingHours: whToBackend(memberForm.workingHours) });
        setMembers(prev => [...prev, res.data.member]);
        toast.success(t('addSuccess', { defaultValue: 'Membro adicionado' }));
      }
      // Sincroniza grupos: adiciona/remove membro dos teamGroups
      await syncMemberGroups(editingMember?.memberId || memberForm.email.replace(/[^a-zA-Z0-9]/g, '_'), memberForm.groups);
      closeMemberModal();
    } catch (e) { toast.error(e.response?.data?.message || e.message); }
    finally { setSavingMember(false); }
  };

  // Sincroniza membros nos teamGroups após salvar membro
  const syncMemberGroups = async (memberId, selectedGroupIds) => {
    for (const tg of teamGroups) {
      const hasMember = (tg.members || []).includes(memberId);
      const shouldHave = selectedGroupIds.includes(tg.teamGroupId);
      if (hasMember === shouldHave) continue;
      const newMembers = shouldHave
        ? [...(tg.members || []), memberId]
        : (tg.members || []).filter(m => m !== memberId);
      await api.put(`/wa-groups/${tenantId}/team-groups/${tg.teamGroupId}`, { name: tg.name, members: newMembers });
    }
    await load();
  };

  const deleteMember = async (m) => {
    const ok = await confirm({ title: t('removeConfirm', { name: m.name }), danger: true });
    if (!ok) return;
    await api.delete(`/teams/${tenantId}/members/${m.memberId}`);
    setMembers(prev => prev.filter(a => a.memberId !== m.memberId));
    toast.success(t('deleteSuccess', { ns: 'common', defaultValue: 'Removido' }));
  };

  const toggleStatus = async (m) => {
    const next = m.status === 'online' ? 'offline' : 'online';
    await api.put(`/teams/${tenantId}/members/${m.memberId}`, { status: next });
    setMembers(prev => prev.map(a => a.memberId === m.memberId ? { ...a, status: next } : a));
  };

  const toggleGroupForMember = (tgId) => {
    const cur = memberForm.groups || [];
    setMemberForm(f => ({ ...f, groups: cur.includes(tgId) ? cur.filter(g => g !== tgId) : [...cur, tgId] }));
  };

  // Atualiza o horário de trabalho do membro (editor compartilhado).
  const setMemberWorkingHours = (wh) => setMemberForm(f => ({ ...f, workingHours: wh }));

  // ── Grupos ─────────────────────────────────────────────────────────────────
  const openCreateGroup = () => { setEditingGroup(null); setGroupForm(EMPTY_GROUP); setGroupModal(true); };
  const openEditGroup = (g) => { setEditingGroup(g); setGroupForm({ name: g.name, members: g.members || [] }); setGroupModal(true); };
  const closeGroupModal = () => { setGroupModal(false); setEditingGroup(null); setGroupForm(EMPTY_GROUP); };

  const saveGroup = async () => {
    if (!groupForm.name) { toast.warning(t('fillRequired')); return; }
    setSavingGroup(true);
    try {
      if (editingGroup) {
        await api.put(`/wa-groups/${tenantId}/team-groups/${editingGroup.teamGroupId}`, groupForm);
        toast.success(t('updateSuccess'));
      } else {
        await api.post(`/wa-groups/${tenantId}/team-groups`, groupForm);
        toast.success(t('addSuccess', { defaultValue: 'Grupo criado' }));
      }
      await load();
      closeGroupModal();
    } catch (e) { toast.error(e.response?.data?.message || e.message); }
    finally { setSavingGroup(false); }
  };

  const deleteGroup = async (g) => {
    const ok = await confirm({ title: t('group.removeConfirm', { name: g.name }), danger: true });
    if (!ok) return;
    await api.delete(`/wa-groups/${tenantId}/team-groups/${g.teamGroupId}`);
    setTeamGroups(prev => prev.filter(x => x.teamGroupId !== g.teamGroupId));
    toast.success(t('deleteSuccess', { ns: 'common', defaultValue: 'Removido' }));
  };

  const toggleMemberForGroup = (memberId) => {
    const cur = groupForm.members || [];
    setGroupForm(f => ({ ...f, members: cur.includes(memberId) ? cur.filter(m => m !== memberId) : [...cur, memberId] }));
  };

  const memberName = (id) => members.find(m => m.memberId === id)?.name || id;
  const groupName  = (id) => teamGroups.find(g => g.teamGroupId === id)?.name || id;
  const connectorName = (id) => connectors.find(c => c.connectorId === id)?.name || id;

  // ── Grupos WA ──────────────────────────────────────────────────────────────
  const openCreateWa = () => { setWaForm(EMPTY_WA_GROUP); setWaModal(true); };
  const closeWaModal = () => { setWaModal(false); setWaForm(EMPTY_WA_GROUP); };

  const saveWaGroup = async () => {
    if (!waForm.connectorId || !waForm.subject) { toast.warning(t('fillRequired')); return; }
    setSavingWa(true);
    try {
      await api.put(`/connectors/${tenantId}/${waForm.connectorId}`, {
        groupsEnabled: true,
        ...(waForm.teamGroupId && { teamGroupId: waForm.teamGroupId })
      });
      await api.post(`/wa-groups/${tenantId}`, {
        connectorId: waForm.connectorId,
        subject: waForm.subject,
        description: waForm.description || '',
        joinApprovalMode: waForm.joinApprovalMode || 'auto_approve'
      });
      toast.success(t('addSuccess', { defaultValue: 'Grupo WA criado' }));
      closeWaModal();
      load();
    } catch (e) { toast.error(e.response?.data?.message || e.message); }
    finally { setSavingWa(false); }
  };

  const deleteWaGroup = async (g) => {
    const ok = await confirm({ title: t('waGroup.deleteConfirm', { subject: g.subject }), danger: true });
    if (!ok) return;
    await api.delete(`/wa-groups/${tenantId}/${g.groupId}`);
    load();
  };

  const copyInviteLink = async (groupId) => {
    try {
      const { data } = await api.get(`/wa-groups/${tenantId}/${groupId}/invite-link`);
      navigator.clipboard.writeText(data.invite_link || '');
      toast.success(t('waGroup.linkCopied'));
    } catch (e) { toast.error(t('waGroup.linkError')); }
  };

  const resetInviteLink = async (groupId) => {
    const ok = await confirm({ title: t('waGroup.resetLinkConfirm') });
    if (!ok) return;
    await api.post(`/wa-groups/${tenantId}/${groupId}/invite-link`);
    toast.success(t('waGroup.linkReset'));
    load();
  };

  const linkTeamGroup = async (connectorId, teamGroupId) => {
    await api.put(`/connectors/${tenantId}/${connectorId}`, { teamGroupId: teamGroupId || null });
    toast.success(t('updateSuccess'));
    load();
  };

  const online     = members.filter(m => m.status === 'online').length;
  const totalChats = members.reduce((s, m) => s + (m.activeChats || 0), 0);

  return (
    <>
      <AppHeader title={t('title')} />
      <BreadcrumbBar><Breadcrumb items={[homeItem, { label: t('title') }]} /></BreadcrumbBar>
      <PageContainer>
        <PageHeader>
          <div>
            <PageTitle>{t('title')}</PageTitle>
            <PageSubtitle>{t('subtitle')}</PageSubtitle>
          </div>
          <Button onClick={tab === 'members' ? openCreateMember : tab === 'groups' ? openCreateGroup : openCreateWa}>
            {tab === 'members' ? <><UserPlus size={16} /> {t('addAgent')}</>
              : tab === 'groups' ? <><Plus size={16} /> {t('group.new')}</>
              : <><Plus size={16} /> {t('waGroup.new')}</>}
          </Button>
        </PageHeader>

        <StatsRow>
          <StatCard accent={theme.colors.primary}>
            <Flex gap={theme.spacing[3]}>
              <StatIconWrap bg={theme.colors.primaryLight} color={theme.colors.primary}><Users2 size={18} /></StatIconWrap>
              <div><StatValue>{members.length}</StatValue><StatLabel>{t('total')}</StatLabel></div>
            </Flex>
          </StatCard>
          <StatCard accent={theme.colors.success}>
            <Flex gap={theme.spacing[3]}>
              <StatIconWrap bg={theme.colors.successLight} color={theme.colors.success}><Wifi size={18} /></StatIconWrap>
              <div><StatValue>{online}</StatValue><StatLabel>{t('online')}</StatLabel></div>
            </Flex>
          </StatCard>
          <StatCard accent={theme.colors.info}>
            <Flex gap={theme.spacing[3]}>
              <StatIconWrap bg={theme.colors.infoLight} color={theme.colors.info}><MessageSquare size={18} /></StatIconWrap>
              <div><StatValue>{totalChats}</StatValue><StatLabel>{t('activeChats')}</StatLabel></div>
            </Flex>
          </StatCard>
          <StatCard accent={theme.colors.warning}>
            <Flex gap={theme.spacing[3]}>
              <StatIconWrap bg={theme.colors.warningLight || '#fef9c3'} color={theme.colors.warning}><Users2 size={18} /></StatIconWrap>
              <div><StatValue>{teamGroups.length}</StatValue><StatLabel>{t('group.plural')}</StatLabel></div>
            </Flex>
          </StatCard>
        </StatsRow>

        <Tabs>
          <Tab $active={tab === 'members'} onClick={() => setTab('members')}>{t('title')} ({members.length})</Tab>
          <Tab $active={tab === 'groups'}  onClick={() => setTab('groups')}>{t('group.plural')} ({teamGroups.length})</Tab>
          <Tab $active={tab === 'wa'}      onClick={() => setTab('wa')}>WhatsApp ({waGroups.length})</Tab>
        </Tabs>

        {loading ? (
          <LoadingContainer><Spinner /><span>{t('loadingTeam')}</span></LoadingContainer>
        ) : tab === 'members' ? (
          members.length === 0 ? (
            <EmptyState><EmptyIcon><Users2 size={40} strokeWidth={1} /></EmptyIcon><EmptyText>{t('noAgents')}</EmptyText></EmptyState>
          ) : (
            <Grid min="320px">
              {members.map(m => (
                <Card key={m.memberId}>
                  <Flex justify="space-between" style={{ marginBottom: theme.spacing[4] }}>
                    <Flex gap={theme.spacing[3]}>
                      <Avatar online={m.status === 'online'}>{m.name.charAt(0)}</Avatar>
                      <div>
                        <AgentName>{m.name}</AgentName>
                        <AgentRole>{m.role}</AgentRole>
                      </div>
                    </Flex>
                    <Badge variant={m.status === 'online' ? 'success' : 'neutral'}>
                      {m.status === 'online' ? <><Wifi size={10} /> {t('online')}</> : <><WifiOff size={10} /> {t('offline')}</>}
                    </Badge>
                  </Flex>
                  <DetailList>
                    <DetailItem><DetailKey>{t('email')}</DetailKey><DetailVal>{m.email}</DetailVal></DetailItem>
                    {m.phone && <DetailItem><DetailKey>{t('phone')}</DetailKey><DetailVal>{m.phone}</DetailVal></DetailItem>}
                    <DetailItem><DetailKey>{t('activeChats')}</DetailKey><DetailVal>{m.activeChats || 0}</DetailVal></DetailItem>
                    {(() => {
                        const memberGroupNames = teamGroups
                          .filter(tg => (tg.members || []).includes(m.memberId))
                          .map(tg => tg.name);
                        return memberGroupNames.length > 0 ? (
                          <DetailItem>
                            <DetailKey>{t('groups')}</DetailKey>
                            <DetailVal>{memberGroupNames.join(', ')}</DetailVal>
                          </DetailItem>
                        ) : null;
                      })()}
                  </DetailList>
                  <Flex gap={theme.spacing[2]} style={{ marginTop: theme.spacing[4] }}>
                    <Button variant="secondary" size="sm" style={{ flex: 1 }} onClick={() => toggleStatus(m)}>
                      {m.status === 'online' ? <><Pause size={13} /> {t('pause')}</> : <><Play size={13} /> {t('activate')}</>}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEditMember(m)}><Pencil size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteMember(m)}><Trash2 size={14} /></Button>
                  </Flex>
                </Card>
              ))}
            </Grid>
          )
        ) : tab === 'groups' ? (
          teamGroups.length === 0 ? (
            <EmptyState><EmptyIcon><Users2 size={40} strokeWidth={1} /></EmptyIcon><EmptyText>{t('group.empty')}</EmptyText></EmptyState>
          ) : (
            <Grid min="300px">
              {teamGroups.map(g => {
                const groupMembers = members.filter(m => (m.groups || []).includes(g.teamGroupId) || (g.members || []).includes(m.memberId));
                return (
                  <Card key={g.teamGroupId}>
                    <Flex justify="space-between" style={{ marginBottom: theme.spacing[3] }}>
                      <GroupName><Users2 size={16} /> {g.name}</GroupName>
                      <Flex gap={theme.spacing[1]}>
                        <Button variant="ghost" size="sm" onClick={() => openEditGroup(g)}><Pencil size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteGroup(g)}><Trash2 size={14} /></Button>
                      </Flex>
                    </Flex>
                    <MemberCount>{t('group.memberCount', { count: groupMembers.length })}</MemberCount>
                    <MemberChips>
                      {groupMembers.length === 0
                        ? <EmptyChip>{t('group.noMembers')}</EmptyChip>
                        : groupMembers.map(m => (
                          <MemberChip key={m.memberId} online={m.status === 'online'}>
                            {m.name.split(' ')[0]}
                          </MemberChip>
                        ))
                      }
                    </MemberChips>
                  </Card>
                );
              })}
            </Grid>
          )
        ) : tab === 'wa' ? (
          <>
            {/* Conectores com groupsEnabled */}
            <SectionTitle>{t('waGroup.connectorsTitle')}</SectionTitle>
            {connectors.length === 0 ? (
              <HintText>{t('waGroup.noConnectors')}</HintText>
            ) : (
              <ConnectorList>
                {connectors.map(c => (
                  <ConnectorRow key={c.connectorId}>
                    <div>
                      <ConnectorRowName>{c.name || c.connectorId}</ConnectorRowName>
                      <ConnectorRowSub>{c.phoneNumberId}</ConnectorRowSub>
                    </div>
                    <Flex gap={theme.spacing[3]} style={{ alignItems: 'center' }}>
                      {c.teamGroupId && (
                        <span style={{ fontSize: 11, color: theme.colors.primary }}>↔ {groupName(c.teamGroupId)}</span>
                      )}
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!c.groupsEnabled} onChange={async e => {
                          await api.put(`/connectors/${tenantId}/${c.connectorId}`, { groupsEnabled: e.target.checked });
                          load();
                        }} />
                        {t('waGroup.groupsEnabled')}
                      </label>
                      <Select
                        style={{ width: 160, fontSize: 12, padding: '4px 8px' }}
                        value={c.teamGroupId || ''}
                        onChange={e => linkTeamGroup(c.connectorId, e.target.value)}
                      >
                        <option value="">{t('waGroup.noInternalGroup')}</option>
                        {teamGroups.map(g => <option key={g.teamGroupId} value={g.teamGroupId}>{g.name}</option>)}
                      </Select>
                    </Flex>
                  </ConnectorRow>
                ))}
              </ConnectorList>
            )}

            {/* Grupos WA */}
            <SectionTitle style={{ marginTop: theme.spacing[6] }}>{t('waGroup.groupsTitle')}</SectionTitle>
            {waGroups.length === 0 ? (
              <EmptyState><EmptyIcon><MessageCircle size={40} strokeWidth={1} /></EmptyIcon><EmptyText>{t('waGroup.empty')}</EmptyText></EmptyState>
            ) : (
              <Grid min="300px">
                {waGroups.map(g => (
                  <Card key={g.groupId || g.requestId}>
                    <Flex justify="space-between" style={{ marginBottom: theme.spacing[2] }}>
                      <div>
                        <GroupName><MessageCircle size={15} /> {g.subject}</GroupName>
                        <ConnectorRowSub>{connectorName(g.connectorId)}</ConnectorRowSub>
                      </div>
                      <Badge variant={g.status === 'active' ? 'success' : g.status === 'pending' ? 'warning' : 'neutral'}>
                        {g.status === 'pending' ? t('waGroup.statusCreating') : g.status === 'active' ? t('waGroup.statusActive') : t('waGroup.statusDeleted')}
                      </Badge>
                    </Flex>
                    {g.description && <ConnectorRowSub style={{ marginBottom: theme.spacing[2] }}>{g.description}</ConnectorRowSub>}
                    {g.inviteLink && (
                      <InviteLinkBox>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11 }}>{g.inviteLink}</span>
                        <Button variant="ghost" size="sm" onClick={() => copyInviteLink(g.groupId)}><Link size={12} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => resetInviteLink(g.groupId)}><RefreshCw size={12} /></Button>
                      </InviteLinkBox>
                    )}
                    <Flex gap={theme.spacing[2]} style={{ marginTop: theme.spacing[3] }}>
                      {!g.inviteLink && g.status === 'active' && (
                        <Button variant="secondary" size="sm" onClick={() => copyInviteLink(g.groupId)}><Link size={13} /> {t('waGroup.link')}</Button>
                      )}
                      {g.status === 'active' && (
                        <Button variant="ghost" size="sm" onClick={() => deleteWaGroup(g)}><Trash2 size={13} /></Button>
                      )}
                    </Flex>
                  </Card>
                ))}
              </Grid>
            )}
          </>
        ) : null}

        {/* ── Modal Membro ── */}
        {memberModal && (
          <ModalOverlay onClick={closeMemberModal}>
            <ModalBox onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{editingMember ? t('editAgent') : t('modalTitle')}</ModalTitle>
                <Button variant="ghost" size="sm" onClick={closeMemberModal} aria-label={t('close', { ns: 'common' })}>✕</Button>
              </ModalHeader>
              <ModalBody>
                <FormGroup>
                  <Label>{t('fullName')}</Label>
                  <Input value={memberForm.name} onChange={e => setMemberForm({ ...memberForm, name: e.target.value })} placeholder="Maria Silva" />
                </FormGroup>
                <FormGroup>
                  <Label>{t('email')}</Label>
                  <Input type="email" value={memberForm.email} onChange={e => setMemberForm({ ...memberForm, email: e.target.value })} placeholder={t('emailPlaceholder')} disabled={!!editingMember} />
                </FormGroup>
                <FormGroup>
                  <Label>{t('role')}</Label>
                  <Select value={memberForm.role} onChange={e => setMemberForm({ ...memberForm, role: e.target.value })}>
                    <option value="">{t('selectRole')}</option>
                    <option value="Atendente">{t('roles.agent')}</option>
                    <option value="Supervisor">{t('roles.supervisor')}</option>
                    <option value="Gerente">{t('roles.manager')}</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>{t('phone')}</Label>
                  <Input value={memberForm.phone} onChange={e => setMemberForm({ ...memberForm, phone: e.target.value })} placeholder="+55 11 98888-9999" />
                </FormGroup>
                <FormGroup>
                  <Label>{t('groups')}</Label>
                  {teamGroups.length === 0 ? (
                    <HintText>{t('group.createHint')}</HintText>
                  ) : (
                    <CheckList>
                      {teamGroups.map(g => (
                        <CheckItem key={g.teamGroupId}>
                          <input
                            type="checkbox"
                            id={`grp-${g.teamGroupId}`}
                            checked={(memberForm.groups || []).includes(g.teamGroupId)}
                            onChange={() => toggleGroupForMember(g.teamGroupId)}
                          />
                          <label htmlFor={`grp-${g.teamGroupId}`}>{g.name}</label>
                        </CheckItem>
                      ))}
                    </CheckList>
                  )}
                </FormGroup>
                <FormGroup>
                  <Label>{t('workingHours', { defaultValue: 'Horário de atendimento' })}</Label>
                  <WorkingHoursEditor
                    value={memberForm.workingHours}
                    onChange={setMemberWorkingHours}
                    t={t}
                    enableLabel={t('workingHoursEnable', { defaultValue: 'Definir horário de atendimento para este membro' })}
                    hint={t('workingHoursHint', { defaultValue: 'Sem horário definido, o membro pode receber transferências a qualquer momento.' })}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>{editingMember ? t('newPassword') : t('password')}</Label>
                  <Input type="password" value={memberForm.password} onChange={e => setMemberForm({ ...memberForm, password: e.target.value })} placeholder={editingMember ? t('passwordEditPlaceholder') : t('passwordPlaceholder')} />
                  {editingMember && <HintText>{t('passwordEditHint')}</HintText>}
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button variant="secondary" onClick={closeMemberModal}>{t('cancel', { ns: 'common' })}</Button>
                <Button onClick={saveMember} disabled={savingMember}>
                  {savingMember ? <><Spinner size="16px" /> {t('saving')}</> : editingMember ? t('save', { ns: 'common' }) : t('addAgent')}
                </Button>
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        )}

        {/* ── Modal Grupo ── */}
        {groupModal && (
          <ModalOverlay onClick={closeGroupModal}>
            <ModalBox onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{editingGroup ? t('group.editTitle') : t('group.new')}</ModalTitle>
                <Button variant="ghost" size="sm" onClick={closeGroupModal} aria-label={t('close', { ns: 'common' })}>✕</Button>
              </ModalHeader>
              <ModalBody>
                <FormGroup>
                  <Label>{t('group.nameLabel')}</Label>
                  <Input value={groupForm.name} onChange={e => setGroupForm({ ...groupForm, name: e.target.value })} placeholder={t('group.namePlaceholder')} />
                </FormGroup>
                <FormGroup>
                  <Label>{t('group.membersLabel')}</Label>
                  {members.length === 0 ? (
                    <HintText>{t('group.noMembersRegistered')}</HintText>
                  ) : (
                    <CheckList>
                      {members.map(m => (
                        <CheckItem key={m.memberId}>
                          <input
                            type="checkbox"
                            id={`mem-${m.memberId}`}
                            checked={(groupForm.members || []).includes(m.memberId)}
                            onChange={() => toggleMemberForGroup(m.memberId)}
                          />
                          <label htmlFor={`mem-${m.memberId}`}>{m.name} <span style={{ color: theme.colors.textMuted, fontSize: 11 }}>({m.role})</span></label>
                        </CheckItem>
                      ))}
                    </CheckList>
                  )}
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button variant="secondary" onClick={closeGroupModal}>{t('cancel', { ns: 'common' })}</Button>
                <Button onClick={saveGroup} disabled={savingGroup}>
                  {savingGroup ? <><Spinner size="16px" /> {t('saving')}</> : editingGroup ? t('save', { ns: 'common' }) : t('group.create')}
                </Button>
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        )}
        {/* ── Modal Grupo WA ── */}
        {waModal && (
          <ModalOverlay onClick={closeWaModal}>
            <ModalBox onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{t('waGroup.modalTitle')}</ModalTitle>
                <Button variant="ghost" size="sm" onClick={closeWaModal} aria-label={t('close', { ns: 'common' })}>✕</Button>
              </ModalHeader>
              <ModalBody>
                <FormGroup>
                  <Label>{t('waGroup.connectorLabel')}</Label>
                  <Select value={waForm.connectorId} onChange={e => setWaForm({ ...waForm, connectorId: e.target.value })}>
                    <option value="">{t('waGroup.selectConnector')}</option>
                    {connectors.map(c => <option key={c.connectorId} value={c.connectorId}>{c.name || c.connectorId}</option>)}
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>{t('waGroup.nameLabel')}</Label>
                  <Input value={waForm.subject} onChange={e => setWaForm({ ...waForm, subject: e.target.value })} maxLength={128} placeholder={t('waGroup.namePlaceholder')} />
                </FormGroup>
                <FormGroup>
                  <Label>{t('description', { ns: 'common' })}</Label>
                  <Input value={waForm.description} onChange={e => setWaForm({ ...waForm, description: e.target.value })} maxLength={2048} />
                </FormGroup>
                <FormGroup>
                  <Label>{t('waGroup.joinModeLabel')}</Label>
                  <Select value={waForm.joinApprovalMode} onChange={e => setWaForm({ ...waForm, joinApprovalMode: e.target.value })}>
                    <option value="auto_approve">{t('waGroup.joinAuto')}</option>
                    <option value="request_to_join">{t('waGroup.joinApproval')}</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>{t('waGroup.linkedGroupLabel')} <span style={{ fontWeight: 400, color: theme.colors.textMuted }}>{t('waGroup.optional')}</span></Label>
                  <Select value={waForm.teamGroupId} onChange={e => setWaForm({ ...waForm, teamGroupId: e.target.value })}>
                    <option value="">{t('waGroup.noInternalGroup')}</option>
                    {teamGroups.map(g => <option key={g.teamGroupId} value={g.teamGroupId}>{g.name}</option>)}
                  </Select>
                  <HintText>{t('waGroup.linkedGroupHint')}</HintText>
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button variant="secondary" onClick={closeWaModal}>{t('cancel', { ns: 'common' })}</Button>
                <Button onClick={saveWaGroup} disabled={savingWa}>
                  {savingWa ? <><Spinner size="16px" /> {t('saving')}</> : t('group.create')}
                </Button>
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        )}
      </PageContainer>
    </>
  );
};

// ── Styled ────────────────────────────────────────────────────────────────────
const BreadcrumbBar = styled.div`background: ${theme.colors.white}; padding: 0 ${theme.spacing[6]}; border-bottom: 1px solid ${theme.colors.border};`;
const StatsRow = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: ${theme.spacing[4]}; margin-bottom: ${theme.spacing[4]};`;
const StatIconWrap = styled.div`width: 40px; height: 40px; border-radius: ${theme.radii.md}; background: ${({ bg }) => bg}; color: ${({ color }) => color}; display: flex; align-items: center; justify-content: center; flex-shrink: 0;`;
const Tabs = styled.div`display: flex; gap: 0; border-bottom: 1px solid ${theme.colors.border}; margin-bottom: ${theme.spacing[5]};`;
const Tab = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[5]}; border: none; background: none; cursor: pointer;
  font-size: ${theme.typography.sm}; font-weight: ${({ $active }) => $active ? theme.typography.semibold : theme.typography.normal};
  color: ${({ $active }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  border-bottom: 2px solid ${({ $active }) => $active ? theme.colors.primary : 'transparent'};
  transition: ${theme.transitions.fast};
`;
const Avatar = styled.div`width: 40px; height: 40px; border-radius: 50%; background: ${({ online }) => online ? theme.colors.success : theme.colors.gray300}; color: white; font-size: ${theme.typography.base}; font-weight: ${theme.typography.bold}; display: flex; align-items: center; justify-content: center; flex-shrink: 0;`;
const AgentName = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary};`;
const AgentRole = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};`;
const DetailList = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[2]}; padding: ${theme.spacing[3]} 0; border-top: 1px solid ${theme.colors.border};`;
const DetailItem = styled.div`display: flex; justify-content: space-between; gap: ${theme.spacing[2]};`;
const DetailKey = styled.span`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};`;
const DetailVal = styled.span`font-size: ${theme.typography.xs}; color: ${theme.colors.textPrimary}; font-weight: ${theme.typography.medium}; text-align: right;`;
const GroupName = styled.div`display: flex; align-items: center; gap: 6px; font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary};`;
const MemberCount = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-bottom: ${theme.spacing[2]};`;
const MemberChips = styled.div`display: flex; flex-wrap: wrap; gap: 6px; margin-top: ${theme.spacing[2]};`;
const MemberChip = styled.div`
  padding: 3px 10px; border-radius: 12px; font-size: 11px;
  background: ${({ online }) => online ? theme.colors.successLight || '#dcfce7' : theme.colors.gray100};
  color: ${({ online }) => online ? theme.colors.success : theme.colors.textSecondary};
`;
const EmptyChip = styled.div`font-size: 11px; color: ${theme.colors.textMuted}; font-style: italic;`;
const CheckList = styled.div`display: flex; flex-direction: column; gap: 8px; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md}; padding: ${theme.spacing[3]}; max-height: 180px; overflow-y: auto;`;
const CheckItem = styled.div`display: flex; align-items: center; gap: 8px; font-size: ${theme.typography.sm}; cursor: pointer; label { cursor: pointer; }`;
const HintText = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: 4px;`;
const SectionTitle = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textSecondary}; margin-bottom: ${theme.spacing[3]}; text-transform: uppercase; letter-spacing: 0.5px; font-size: 11px;`;
const ConnectorList = styled.div`display: flex; flex-direction: column; gap: 0; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md}; overflow: hidden;`;
const ConnectorRow = styled.div`display: flex; justify-content: space-between; align-items: center; padding: ${theme.spacing[3]} ${theme.spacing[4]}; border-bottom: 1px solid ${theme.colors.border}; background: ${theme.colors.white}; &:last-child { border-bottom: none; }`;
const ConnectorRowName = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.medium}; color: ${theme.colors.textPrimary};`;
const ConnectorRowSub = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};`;
const InviteLinkBox = styled.div`display: flex; align-items: center; gap: 6px; background: ${theme.colors.gray50}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md}; padding: 4px 8px; margin-top: ${theme.spacing[2]};`;

export default TeamManagement;
